// TablaHabitaciones.jsx
import React, { useCallback, useState } from "react";
import Select from "react-select";
import { useDispatch } from "react-redux";
import { traerSalones } from "../../../../store/cotizaciones/thunkCotizaciones";
import { ExtraerArticulo, obtenerListaArticulosSegunGrupo } from "../../../../store/Articulos/thunkArticulos";
import { EditarArticulo } from "../../Articulos/EditarArticulo";

/**
 * Objeto base de un requerimiento, sirve como plantilla para crear nuevos registros.
 */
const requerimientoVacio = {
    id: 1,
    fechaUno: "",
    fechaDos: "",
    noches: "",
    descripcionArticulo: "",
    cantidadHabitaciones: 0, // Calculado según noches y artículos seleccionados
    precio: "",
    subtotal: "",
    articulosOptions: [],
    hotel: "", // Añadido para almacenar el hotel seleccionado
};

/**
 * Formatea un número a dos decimales.
 */
const formatter = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

/**
 * Limpia un subtotal en formato string, extrayendo solo valores numéricos.
 * @param {string} valor - El valor del subtotal en formato string.
 * @returns {number} - El subtotal limpio como número flotante.
 */
const limpiarSubtotal = (valor) => parseFloat((valor || "").toString().replace(/[^0-9.]/g, "")) || 0;

/**
 * Calcula los totales (subtotal y total con impuestos) a partir del estado actual del formulario.
 * @param {Array} requerimientoshabitaciones - La lista de servicios adicionales.
 * @returns {Object} - Un objeto con los totales calculados.
 */
const calcularTotales = (requerimientoshabitaciones, mucama) => {
    let totalGlobal = 0;

    requerimientoshabitaciones.forEach((item) => {
        totalGlobal += limpiarSubtotal(item.subtotal);
    });

    const subtotal = totalGlobal;
    const impuestoVenta = (13 / 100) * totalGlobal; // 13% del subtotal

    // Asegurar que 'mucama' sea un número (por si viene como string)
    const mucamaNumerico = requerimientoshabitaciones.length === 0 ? 0 : parseFloat(mucama) || 0;

    // Calcular total asegurando que todos sean números
    const totalAB = subtotal + impuestoVenta + mucamaNumerico;

    return {
        subtotal: formatter.format(subtotal),
        impuestoVenta: formatter.format(impuestoVenta),
        impuestoServicio: formatter.format(mucamaNumerico), // Formatear también por consistencia
        totalAB: formatter.format(totalAB),
    };
};

/**
 * Dado un requerimiento, recalcula el subtotal en base a noches * cantidad * precio.
 * @param {Object} req - El requerimiento a recalcular.
 * @returns {string} - El subtotal formateado.
 */
const recalcularSubtotalRequerimiento = (req) => {
    const noches = parseFloat(req.noches || 0);
    const cantidad = parseFloat(req.cantidadHabitaciones || 0);
    const precio = parseFloat(req.precio || 0);
    const subtotal = noches * cantidad * precio;
    return formatter.format(subtotal);
};

/**
 * Calcula la cantidad de noches entre dos fechas.
 * @param {string} fechaInicio - La fecha de inicio en formato 'YYYY-MM-DD'.
 * @param {string} fechaFin - La fecha de fin en formato 'YYYY-MM-DD'.
 * @returns {number} - La cantidad de noches.
 */
const calcularNoches = (fechaInicio, fechaFin) => {
    if (!fechaInicio || !fechaFin) return 0;
    const start = new Date(fechaInicio);
    const end = new Date(fechaFin);
    const diffTime = end - start;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
};

/**
 * Componente principal. Muestra y gestiona la tabla de requerimientoshabitaciones.
 * @param {Object} props - Las propiedades del componente.
 * @param {Object} props.formulario - El estado del formulario que contiene requerimientoshabitaciones.
 * @param {Function} props.setFormulario - La función para actualizar el estado del formulario.
 * @param {Array} props.hotelesOpciones - Las opciones disponibles para seleccionar hoteles.
 */
export const TablaHabitaciones = ({ formulario, setFormulario, hotelesOpciones }) => {
    const dispatch = useDispatch();
    const [lineCounter, setLineCounter] = useState(1);
    const [openEditModal, setOpenEditModal] = useState(false);
    const [selectedArticulo, setSelectedArticulo] = useState(null);

    /**
     * Obtiene la lista completa de artículos.
     * @returns {Array} - Las opciones de artículos.
     */
    const fetchArticulosAPI = useCallback(async () => {
        const articulos = await dispatch(obtenerListaArticulosSegunGrupo(1, "Rooms"));
        return articulos.map((art) => ({
            value: art.id_articulo,
            label: !art.hotelesSeleccionados || art.hotelesSeleccionados.length === 0 ? `Sin hotel / ${art.nombre_articulo}` : art.nombre_articulo,
        }));
    }, [dispatch]);

    /**
     * Agrega un nuevo requerimiento, obteniendo las opciones de artículos.
     */
    const agregarRequerimiento = async () => {
        const newArticulosOptions = await fetchArticulosAPI();
        const nuevoReq = {
            ...requerimientoVacio,
            id: lineCounter + 1,
            articulosOptions: newArticulosOptions,
            hotel: formulario.hotelSeleccionado || "",
        };

        setFormulario((prev) => ({
            ...prev,
            requerimientoshabitaciones: [...prev.requerimientoshabitaciones, nuevoReq],
        }));
        setLineCounter((count) => count + 1);
    };

    /**
     * Dado el ID de un artículo y el hotel seleccionado, actualiza la descripción, precio y subtotal.
     * @param {number} index - El índice del requerimiento en la lista.
     * @param {number} idArticulo - El ID del artículo seleccionado.
     * @param {number} hotelSeleccionado - El ID del hotel seleccionado.
     */
    const actualizarCamposSegunArticulo = async (index, idArticulo, hotelSeleccionado) => {
        try {
            const articulo = await dispatch(ExtraerArticulo(idArticulo));
            let precioFinal = articulo.precios_especificos?.[hotelSeleccionado] || articulo.precio_art;

            if (!articulo.precios_especificos?.[hotelSeleccionado]) {
                alert("No se encontró un precio específico para el hotel seleccionado. Se usará el precio base.");
            }

            // Actualizamos el requerimiento con la descripción y el precio
            setFormulario((prev) => {
                const reqs = [...prev.requerimientoshabitaciones];
                reqs[index] = {
                    ...reqs[index],
                    descripcionArticulo: articulo.desc_articulo,
                    precio: precioFinal,
                    subtotal: recalcularSubtotalRequerimiento({ ...reqs[index], precio: precioFinal }),
                };

                return {
                    ...prev,
                    requerimientoshabitaciones: reqs,
                    totalHabitaciones: calcularTotales(reqs, formulario.totalHabitaciones.impuestoServicio),
                };
            });
        } catch (error) {
            console.error("Error al actualizar descripción del artículo:", error);
        }
    };

    /**
     * Actualiza uno o varios campos de un requerimiento.
     * @param {number} index - El índice del requerimiento en la lista.
     * @param {string} campo - El campo a actualizar.
     * @param {any} valor - El nuevo valor del campo.
     */
    const actualizarCampoRequerimiento = (index, campo, valor) => {
        setFormulario((prev) => {
            const reqs = [...prev.requerimientoshabitaciones];

            // Actualiza el requerimiento según si campo es string u objeto
            if (typeof campo === "string") {
                reqs[index] = { ...reqs[index], [campo]: valor };
            } else {
                reqs[index] = { ...reqs[index], ...campo };
            }

            // Recalcula subtotal si es necesario
            if (["noches", "cantidadHabitaciones", "precio"].includes(campo)) {
                reqs[index].subtotal = recalcularSubtotalRequerimiento(reqs[index]);
            }

            // Si se actualiza fechaUno o fechaDos, recalcula las noches
            if (campo === "fechaUno" || campo === "fechaDos") {
                const fechaUno = reqs[index].fechaUno;
                const fechaDos = reqs[index].fechaDos;
                const nochesCalculadas = calcularNoches(fechaUno, fechaDos);
                reqs[index].noches = nochesCalculadas;
                reqs[index].subtotal = recalcularSubtotalRequerimiento(reqs[index]);
            }

            return {
                ...prev,
                requerimientoshabitaciones: reqs,
                totalHabitaciones: calcularTotales(reqs, formulario.totalHabitaciones.impuestoServicio),
            };
        });
    };

    /**
     * Elimina un requerimiento por índice.
     * @param {number} index - El índice del requerimiento a eliminar.
     */
    const eliminarRequerimiento = (index) => {
        setFormulario((prev) => {
            const reqs = prev.requerimientoshabitaciones.filter((_, i) => i !== index);
            return {
                ...prev,
                requerimientoshabitaciones: reqs,
                totalHabitaciones: calcularTotales(reqs, formulario.totalHabitaciones.impuestoServicio),
            };
        });
    };


    const clonarLinea = (datos, index) => {
        // Crea un nuevo objeto 'nuevoReq' clonando el objeto 'datos' recibido
        // y asigna un nuevo id basado en el índice recibido más 1.
        const nuevoReq = {
            ...datos, // Copia todas las propiedades de 'datos'.
            id: index + 1, // Genera un nuevo id único sumando 1 al índice.
        };

        // Actualiza el estado del formulario utilizando la función setFormulario.
        setFormulario((prev) => {
            // Crea un nuevo array de 'otroServicios' añadiendo el nuevo requerimiento.
            const nuevosRequerimientos = [...prev.requerimientoshabitaciones, nuevoReq];

            // Retorna el nuevo estado del formulario actualizando 'otroServicios'
            // y recalculando el total de servicios utilizando la función 'calcularTotales'.
            return {
                ...prev, // Copia todas las propiedades del estado actual.
                requerimientoshabitaciones: nuevosRequerimientos, // Actualiza la lista de servicios.
                totalHabitaciones: calcularTotales(nuevosRequerimientos, formulario.totalHabitaciones.impuestoServicio), // Calcula el nuevo total de servicios.
            };
        });

        // Incrementa el contador de líneas en 2 y actualiza su estado.
        setLineCounter((count) => count + 2);
    };

    /**
     * Cambia el hotel de un requerimiento, actualizando artículos.
     * @param {number} index - El índice del requerimiento en la lista.
     * @param {Object} selectedOption - La opción seleccionada del Select.
     */
    const handleHotelChange = async (index, selectedOption) => {
        try {
            const newArticulosOptions = await fetchArticulosAPI();

            actualizarCampoRequerimiento(index, {
                hotel: selectedOption.value,
                descripcionArticulo: "",
                precio: 0,
                subtotal: 0,
                articulosOptions: newArticulosOptions,
            });
        } catch (error) {
            console.error("Error al cambiar de hotel:", error);
        }
    };

    /**
     * Cierra el modal de edición de artículo.
     */
    const handleCloseEditModal = () => {
        setSelectedArticulo(null);
        setOpenEditModal(false);
    };

    /**
     * Abre el modal de edición de un artículo seleccionado.
     * @param {number} idArticulo - El ID del artículo a editar.
     */
    const mostrarIdArticulo = async (idArticulo) => {
        if (!idArticulo) {
            alert("Seleccione un artículo para editar");
            return;
        }
        const articulo = await dispatch(ExtraerArticulo(idArticulo));
        setSelectedArticulo(articulo);
        setOpenEditModal(true);
    };

    return (
        <div className="col-12">
            {/* Título de la sección */}
            <div style={{ textAlign: "center", backgroundColor: "#000", color: "#fff" }} className="alert alert-secondary" role="alert">
                REQUERIMIENTOS DE HABITACIONES
            </div>

            {/* Tabla de requerimientos */}
            <div className="table-responsive" style={{ overflowX: "auto", width: "100%" }}>
                <table className="table table-hover mb-0" style={{ minWidth: "1500px" }}>
                    <thead>
                        <tr>
                            <th>Seleccione Hotel</th>
                            <th>FECHA INGRESO</th>
                            <th>FECHA SALIDA</th>
                            <th>CANTIDAD NOCHES</th>
                            <th>TIPO DE HABITACIÓN</th>
                            <th>DEC DE HABITACIÓN</th>
                            <th>CANT. HABI.</th>
                            <th>TAR. NOCHE</th>
                            <th>Subtotal</th>
                            <th>Eliminar</th>
                        </tr>
                    </thead>
                    <tbody>
                        {formulario.requerimientoshabitaciones.map((req, index) => (
                            <tr key={req.id}>
                                {/* Selección de Hotel */}
                                <td style={{ minWidth: 210 }}>
                                    <Select
                                        options={[...hotelesOpciones, { value: 100, label: "Precio estándar" }]}
                                        value={
                                            req.hotel === 100
                                                ? { value: 100, label: "Precio estándar" }
                                                : hotelesOpciones.find((opt) => opt.value === req.hotel) || null
                                        }
                                        onChange={(selected) => handleHotelChange(index, selected)}
                                        placeholder="Seleccione hotel"
                                        menuPortalTarget={document.body}
                                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                    />
                                </td>

                                {/* Fecha de Entrada */}
                                <td>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={req.fechaUno}
                                        onChange={(e) => actualizarCampoRequerimiento(index, "fechaUno", e.target.value)}
                                    />
                                </td>

                                {/* Fecha de Salida */}
                                <td>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={req.fechaDos}
                                        onChange={(e) => actualizarCampoRequerimiento(index, "fechaDos", e.target.value)}
                                    />
                                </td>

                                {/* Cantidad de Noches */}
                                <td>
                                    <input
                                        style={{ minWidth: 80 }}
                                        type="number"
                                        className="form-control"
                                        value={req.noches}
                                        onChange={(e) => actualizarCampoRequerimiento(index, "noches", e.target.value)}
                                    />
                                </td>

                                {/* Selección de Artículo en lugar de Tipo de Habitación */}
                                <td style={{ minWidth: 260, position: "relative" }}>
                                    <Select
                                        options={req.articulosOptions}
                                        value={req.articulosOptions.find((opt) => opt.value === req.tipoHabitacion) || null}
                                        onChange={(selectedOption) => {
                                            const value = selectedOption?.value || "";
                                            actualizarCampoRequerimiento(index, "tipoHabitacion", value);
                                            actualizarCamposSegunArticulo(index, value, req.hotel);
                                        }}
                                        placeholder="Seleccione tipo de habitación"
                                        menuPortalTarget={document.body}
                                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                    />
                                    <button
                                        type="button"
                                        className="btn btn-link position-absolute"
                                        style={{ top: -17, right: 5, fontSize: "1.2rem", color: "#007bff" }}
                                        onClick={() => mostrarIdArticulo(req.tipoHabitacion)}
                                    >
                                        🖉
                                    </button>
                                </td>

                                {/* Descripción del Artículo */}
                                <td>
                                    <textarea
                                        className="form-control form-control-sm"
                                        placeholder="Descripción"
                                        rows={2}
                                        style={{ width: "248px", resize: "both" }}
                                        value={req.descripcionArticulo}
                                        onChange={(e) => actualizarCampoRequerimiento(index, "descripcionArticulo", e.target.value)}
                                    />
                                </td>

                                {/* Cantidad de Habitaciones */}
                                <td>
                                    <input
                                        style={{ minWidth: 80 }}
                                        type="number"
                                        className="form-control"
                                        value={req.cantidadHabitaciones}
                                        onChange={(e) => actualizarCampoRequerimiento(index, "cantidadHabitaciones", e.target.value)}
                                    />
                                </td>

                                {/* Tarifa por Noche */}
                                <td>
                                    <input
                                        style={{ minWidth: 110 }}
                                        type="text"
                                        className="form-control"
                                        value={req.precio}
                                        onChange={(e) => actualizarCampoRequerimiento(index, "precio", e.target.value)}
                                    />
                                </td>

                                {/* Subtotal (Solo lectura) */}
                                <td>
                                    <input
                                        style={{ minWidth: 110 }}
                                        type="text"
                                        className="form-control"
                                        value={req.subtotal}
                                        onChange={(e) => actualizarCampoRequerimiento(index, "subtotal", e.target.value)}
                                    />
                                </td>

                                {/* Eliminar Requerimiento */}
                                <td>

                                    <button
                                        type="button"
                                        className="btn btn-danger"
                                        style={{ marginBottom: "10px", display: "block" }} // Espaciado y alineación en bloque
                                        onClick={() => eliminarRequerimiento(index)}
                                    >
                                        <i className="ti ti-trash"></i>
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        style={{ display: "block" }} // Asegura que los botones no estén alineados horizontalmente
                                        onClick={() => clonarLinea(req, index)}
                                    >
                                        <i className="ti ti-copy"></i>
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Botón para agregar una nueva habitación */}
            <div className="text-end mt-3">
                <button type="button" className="btn btn-dark" onClick={agregarRequerimiento}>
                    + Agregar Habitación
                </button>
                <hr className="mb-2 mt-3" />
            </div>

            {/* Modal para editar artículo */}
            {selectedArticulo && (
                <EditarArticulo
                    open={openEditModal}
                    handleClose={handleCloseEditModal}
                    fetchArticulos={fetchArticulosAPI}
                    type="lista"
                    Articulo={selectedArticulo}
                />
            )}
        </div>
    );
};
